import { Inject, Injectable } from '@angular/core';
import { arrayUpsert } from '@datorama/akita';
import { HttpClient } from '@angular/common/http';
import { map, tap } from 'rxjs/operators';
import { StrapiCollectionResponse, StrapiEntity } from '../global/global.model';
import { environment } from '../../../../environments/environment';
import qs from 'qs';
import { Project, ProjectsQuery, ProjectsStore } from '../project';
import { PublicEncodeJob } from '../../../../../../api/src/encode/encode-job.entity';
import { ReviewerRole } from '../../../pages/review/review-reviewers-dialog/review-reviewers-dialog.component';
import { AddReviewersDto } from '../../../../../../api/src/project/models/add-reviewers.dto';
import { RemoveReviewersDto } from '../../../../../../api/src/project/models/remove-reviewers.dto';
import { UpdateReviewerDto } from '../../../../../../api/src/project/models/update-reviewer.dto';
import { AddCommentDto } from '../../../../../../api/src/comment/dtos/add-comment.dto';
import { DefaultResponse } from '../session';
import { QueryParams } from '../collection/collection.model';
import { TextSingularPluralPipe } from '../../../shared/pipes/text-singular-plural.pipe';
import { ExportType } from '../../../../../../api/src/creative-unit-package/creative-unit-package.entity';
import { PublicComment } from '../../../../../../api/src/comment/comment.entity';
import { stringToFilename } from '../../utils/string.utils';
import { CartItem } from '../cart/cart.model';
import { removeObjectValues } from '../../utils/object.utils';
import { DOCUMENT } from "@angular/common";
import { GlobalService } from "../global";

@Injectable({ providedIn: 'root' })
export class EncodeJobService {
	constructor(
		private projectsStore: ProjectsStore,
		private projectsQuery: ProjectsQuery,
		private globalService: GlobalService,
		private http: HttpClient,
		private textSingularPluralPipe: TextSingularPluralPipe,
		@Inject(DOCUMENT) private document: Document
	) {}

	get() {
		return this.http
			.get<DefaultResponse<PublicEncodeJob[]>>(`${environment.apiUrl}/encode/list/${this.projectsQuery.getActiveId()}`)
			.pipe(
				map(response => ({
					...response,
					data: response.data
				}))
			);
	}

	getExportHistoryDetails(projectId: string, encodeJobId: string) {
		return this.http
			.get<DefaultResponse<any>>(`${environment.apiUrl}/encode/export-history/${projectId}/${encodeJobId}`)
			.pipe(map(response => response.data))
	}

	findExportTableData({ page, limit, sortBy, query }: QueryParams) {
		const sortOption: any = {};
		if (sortBy) {
			sortOption.sortBy = sortBy.id;
			sortOption.sort = sortBy.direction;
		}

		if (query) {
			sortOption.query = query;
		}

		return this.http
			.get<DefaultResponse<PublicEncodeJob[]>>(`${environment.apiUrl}/encode/export-history/${this.projectsQuery.getActiveId()}`, {
				params: { page, limit, ...sortOption }
			})
			.pipe(
				map(response => {
					return {
						...response,
						data: response.data.map(job => {
							const metadatas = job.encodeTasks?.map(task => task.metadata) || [];

							const videos = metadatas.filter(metadata => metadata.isVideo) || [];
							const ads = metadatas.filter(metadata => metadata.isAd) || [];

							let videoText: string, adText: string;
							if (videos.length > 0) {
								videoText = this.textSingularPluralPipe.transform(videos.length, $localize `:Export History Creative Packages Content@@exportHistoryVideoLabel:Video` , $localize `:Export History Creative Packages Content@@exportHistoryVideosLabel:Videos`);
							}
							if (ads.length > 0) {
								adText = this.textSingularPluralPipe.transform(ads.length, $localize `:Export History Creative Packages Content@@exportHistoryAdLabel:Size`, $localize `:Export History Creative Packages Content@@exportHistoryAdsLabel:Sizes`);
							}

							let formats =
								metadatas.flatMap(metadata => {
									return (metadata?.formats || [ExportType.VIDEO]).map(format =>
										ExportType.getExportTypeFileFormat(format, metadata.outputConfig)
									);
								}) || [];

							return {
								packages: [videoText, adText].filter(Boolean).join(', '),
								formats: [...new Set(formats)].join(', '),
								...job
							};
						})
					};
				})
			);
	}

	public startReview(projectId: string) {
		return this.http.post<DefaultResponse<Project>>(`${environment.apiUrl}/projects/${projectId}/review/start`, {}).pipe(
			tap(response => {
				this.projectsStore.upsert(response.data.id, response.data);
			})
		);
	}

	public retryJob(jobId: string) {
		return this.http.post(`${environment.apiUrl}/encode/job/${jobId}/retry`, {}).pipe(
			tap(response => {
				console.log(response);
			})
		);
	}

	public retryTask(taskId: string) {
		return this.http.post(`${environment.apiUrl}/encode/task/${taskId}/retry`, {}).pipe(
			tap(response => {
				console.log(response);
			})
		);
	}

	public addReviewers(project: Project, encodeJob: PublicEncodeJob, emails: string[], role: ReviewerRole) {
		this.projectsStore.setLoading(true);

		const params: AddReviewersDto = {
			[role + 's']: {
				emails
			}
		};

		return this.http
			.put<DefaultResponse<PublicEncodeJob>>(
				`${environment.apiUrl}/projects/${project.id}/review/job/${encodeJob.id}/viewers`,
				params
			)
			.pipe(
				tap(response => {
					this.projectsStore.upsert(response.data.projectId, {
						encodeJobs: arrayUpsert(
							this.projectsQuery.getEntity(response.data.projectId).encodeJobs,
							response.data.id,
							response.data
						)
					});
					this.projectsStore.setLoading(false);
				})
			);
	}

	public updateReviewer(project: Project, encodeJob: PublicEncodeJob, reviewerId: string, isApprover: boolean) {
		this.projectsStore.setLoading(true);

		const params: UpdateReviewerDto = {
			isApprover
		};

		return this.http
			.put<DefaultResponse<PublicEncodeJob>>(
				`${environment.apiUrl}/projects/${project.id}/review/job/${encodeJob.id}/viewers/${reviewerId}`,
				params
			)
			.pipe(
				tap(response => {
					console.log('updateReviewer', response);
					this.projectsStore.upsert(response.data.projectId, {
						encodeJobs: arrayUpsert(
							this.projectsQuery.getEntity(response.data.projectId).encodeJobs,
							response.data.id,
							response.data
						)
					});
					this.projectsStore.setLoading(false);
				})
			);
	}

	public removeReviewers(project: Project, encodeJob: PublicEncodeJob, ids: string[], role: ReviewerRole) {
		this.projectsStore.setLoading(true);

		const params: RemoveReviewersDto = {
			[role + 's']: ids
		};

		return this.http
			.delete<DefaultResponse<PublicEncodeJob>>(`${environment.apiUrl}/projects/${project.id}/review/job/${encodeJob.id}/viewers`, {
				body: params
			})
			.pipe(
				tap(response => {
					this.projectsStore.upsert(response.data.projectId, {
						encodeJobs: arrayUpsert(
							this.projectsQuery.getEntity(response.data.projectId).encodeJobs,
							response.data.id,
							response.data
						)
					});
					this.projectsStore.setLoading(false);
				})
			);
	}

	public downloadFromUrl(taskId: string) {
		return this.http.get(`${environment.apiUrl}/encode/task/${taskId}/download`, { responseType: 'blob' });
	}

	public bulkGetFromUrl(jobId: string, page?: number, limit?: number, accessKey?: string) {
		let query = {};
		if (page) query['page'] = page;
		if (limit) query['limit'] = limit;
		return this.http.get(`${environment.apiUrl}/encode/job/${jobId}/download/${accessKey || ''}`, { responseType: 'blob', params: query });
	}

	public getFilteredItems(jobId: string, params: {cartItemId?: CartItem['id'], packageId?: string, format?: ExportType} = {}, accessKey?: string) {
		return this.http.get(`${environment.apiUrl}/encode/job/${jobId}/filtered/download/${accessKey || ''}`, { params: {...removeObjectValues(params)}, responseType: 'blob'});
	}

	public CSVGetFromUrl(jobId: string) {
		return this.http.get(`${environment.apiUrl}/encode/job/${jobId}/download-csv`, { responseType: 'blob' });
	}

	public downloadExportFromBlob(data: Blob, filename?: string) {
		if (!filename) {
			let date = new Date().getFullYear() + '-' + (new Date().getMonth() + 1) + '-' + new Date().getDate();
			date = date.concat(' at ' + new Date().getHours() + '-' + new Date().getMinutes() + '-' + new Date().getSeconds());

			filename = 'export-' + date;
		}

		const url = this.document.defaultView.URL.createObjectURL(data);
		const link = this.document.createElement('a');
		link.href = url;
		link.download = stringToFilename(filename);
		link.click();
		this.document.defaultView.URL.revokeObjectURL(url);
	}

	public exportCollectionEntity(entity: any) {
		return this.bulkGetFromUrl(entity.id).subscribe({
			next: (data: Blob) => {
				this.downloadExportFromBlob(data);
			},
			error: () => {
				this.globalService.triggerErrorMessage(
					undefined,
					$localize `:Error Message@@collectionContainerExportEntityErrorMessageText:There was an error downloading the export.`
				);
			}
		});
	}

}
